import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react'
import Confirmation from './confirmation'
import GeneralModal from '../overlays/generalModal'

enum ModalIds {
  Confirmation = 'confirmation',
  General = 'general',
}

interface ModalState {
  activeModalId: string | null
  modalProps: any
  // Tracks which useApplicationModal instance opened the modal so only that
  // instance auto-syncs props — prevents conflicts when multiple hooks share the same modal ID.
  ownerId: symbol | null
}

interface ApplicationModalsProviderProps {
  children: ReactNode
  /**
   * Additional modal renderers that extend the base set (general + confirmation).
   * This allows packages like ADC to register authentication and verification modals
   * without core needing to know about those components.
   */
  additionalModals?: Record<
    string,
    (props: { close: () => void; modalProps: any }) => React.ReactNode | null
  >
  capabilities?: Record<string, any>
  modalIntents?: Record<string, string>
}

const defaultContext = {
  open: (_modalId: string, _props?: any, _ownerId?: symbol) => {},
  update: (_props: any, _modalId?: string, _ownerId?: symbol) => {},
  close: () => {},
  getActiveModalId: () => '' as string | null,
  getActiveOwnerId: () => null as symbol | null,
  modalIds: ModalIds,
}

const ApplicationModalContext = createContext(defaultContext)

const initialState: ModalState = {
  activeModalId: null,
  modalProps: null,
  ownerId: null,
}

const applicationModalReducer = (
  state: ModalState,
  action: any
): ModalState => {
  switch (action.type) {
    case 'OPEN_ACTIVE_MODAL': {
      const ownerId = action.ownerId ?? null
      // Idempotent: same id + same props ref + same owner = no new state object, prevents re-render loops
      if (
        state.activeModalId === action.modalId &&
        state.modalProps === action.modalProps &&
        state.ownerId === ownerId
      ) {
        return state
      }
      return {
        activeModalId: action.modalId,
        modalProps: action.modalProps,
        ownerId,
      }
    }
    case 'UPDATE_ACTIVE_MODAL': {
      if (!state.activeModalId) return state
      // When a modalId is provided, only apply if it matches the active modal
      if (action.modalId && state.activeModalId !== action.modalId) return state
      // When a useApplicationModal hook owns the modal, only that hook may update it.
      // Raw update() calls (no ownerId) are blocked to prevent cross-modal corruption.
      if (state.ownerId && action.ownerId !== state.ownerId) return state
      return {
        ...state,
        modalProps: { ...state.modalProps, ...action.modalProps },
      }
    }
    case 'CLOSE_ACTIVE_MODAL': {
      if (!state.activeModalId) return state
      return { activeModalId: null, modalProps: null, ownerId: null }
    }
    default: {
      return state
    }
  }
}

const ApplicationModalsProvider = ({
  children,
  additionalModals = {},
  capabilities = {},
  modalIntents = {},
}: ApplicationModalsProviderProps) => {
  const [state, dispatch] = useReducer(applicationModalReducer, initialState)

  const open = useCallback(
    (modalId: string, modalProps?: any, ownerId?: symbol) => {
      dispatch({
        type: 'OPEN_ACTIVE_MODAL',
        modalId,
        modalProps: modalProps ?? null,
        ownerId,
      })
    },
    [dispatch]
  )

  const update = useCallback(
    (modalProps: any, modalId?: string, ownerId?: symbol) => {
      dispatch({ type: 'UPDATE_ACTIVE_MODAL', modalProps, modalId, ownerId })
    },
    [dispatch]
  )

  const close = useCallback(() => {
    dispatch({ type: 'CLOSE_ACTIVE_MODAL' })
  }, [dispatch])

  const getActiveModalId = useCallback(
    () => state.activeModalId,
    [state.activeModalId]
  )

  const getActiveOwnerId = useCallback(() => state.ownerId, [state.ownerId])

  const context = useMemo(
    () => ({
      open,
      update,
      close,
      getActiveModalId,
      getActiveOwnerId,
      modalIntents,
      modalIds: ModalIds,
    }),
    [open, update, close, getActiveModalId, getActiveOwnerId, modalIntents]
  )

  // Render the modal as inline JSX instead of an inner component to preserve
  // DOM identity (and therefore input focus) across state updates.
  const { activeModalId, modalProps: currentModalProps } = state
  const resolvedProps = currentModalProps || {}

  let selectedModal: React.ReactElement | null = null
  if (activeModalId) {
    if (additionalModals[activeModalId]) {
      selectedModal = additionalModals[activeModalId]({
        close,
        modalProps: resolvedProps,
      }) as React.ReactElement | null
    } else {
      switch (activeModalId) {
        case ModalIds.Confirmation:
          selectedModal = capabilities.confirmation?.enabled ? (
            <Confirmation close={close} {...resolvedProps} />
          ) : null
          break
        case ModalIds.General:
          selectedModal = (
            <GeneralModal open onClose={close} {...resolvedProps} />
          )
          break
        default:
          break
      }
    }
  }

  return (
    <ApplicationModalContext.Provider value={context}>
      {selectedModal}
      {children}
    </ApplicationModalContext.Provider>
  )
}

/**
 * @deprecated Calling `useApplicationModals()` without a `modalId` returns the
 * raw context which bypasses ownership protection — concurrent callers can
 * overwrite each other's modal props. Pass a `modalId` (and optional
 * `modalProps`) to get an owner-scoped `{ open, close }` API instead.
 */
function useApplicationModals(): typeof defaultContext
/**
 * Owner-scoped hook for application modals.
 *
 * Returns `{ open, close }`. Props changes are automatically synced while the
 * modal is open, and only the hook instance that called `open()` may update
 * the modal — preventing cross-caller overwrites.
 */
function useApplicationModals(
  modalId: string,
  modalProps?: any
): { open: () => void; close: () => void }
function useApplicationModals(modalId?: string, modalProps?: any) {
  const context = useContext(ApplicationModalContext)

  if (context === defaultContext) {
    console.warn(
      'useApplicationModals is being used without an ApplicationModalsProvider. Modal operations will be no-ops.'
    )
  }

  const {
    open: openModal,
    update,
    close,
    getActiveModalId,
    getActiveOwnerId,
  } = context

  const instanceId = useRef(Symbol('useApplicationModals')).current

  const open = useCallback(() => {
    openModal(modalId!, modalProps, instanceId)
  }, [openModal, modalId, modalProps, instanceId])

  // Auto-sync props only when this instance owns the active modal
  useEffect(() => {
    if (
      modalId &&
      getActiveOwnerId() === instanceId &&
      getActiveModalId() === modalId &&
      modalProps
    ) {
      update(modalProps, modalId, instanceId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, modalProps])

  // Owner-scoped API when called with a modalId
  if (modalId) return { open, close }

  // Raw context when called without arguments
  return context
}

export {
  ApplicationModalContext,
  ApplicationModalsProvider,
  useApplicationModals,
  ModalIds,
}

export type { ApplicationModalsProviderProps }
